import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from './firebase';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import './Onboarding.css';
import { useNavigate } from 'react-router-dom';
// Audio Assets
import clickSfx from './assets/edit.mp3'; 
import successSfx from './assets/create.mp3'; 
import backSfx from './assets/enter.mp3';
import { updateProfile } from 'firebase/auth';
const INTEREST_OPTIONS = ["AI", "Productivity", "Journaling", "Fitness", "Design", "Web3", "Health", "Finance", "Travel", "Music", "Art", "Writing", "Coding", "Gaming", "Philosophy", "Psychology", "Business", "Marketing", "Startups", "Leadership", "Mindfulness"];
const media = [
  // Music (compressed)
  "South Indian Music",
  "Bollywood Music",
  "Peppy hits",
  "Romantic Songs",
  "International Pop",
  "Gossips",
  "Movies",
  "TV Shows",
  "Web Series",
  "Anime",
  "YouTube",
  "Music Videos",
  "Short Films",
  "Books",
  "Gaming Streams",
  "Live Streams",
  "News"
];

const hobbies = [
  "Cooking",
  "Fitness",
  "Yoga",
  "Meditation",
  "Traveling",
  "Photography",
  "Reading",
  "Writing",
  "Music",
  "Art & Design",
  "Gardening",
  "DIY Projects"
];

const languages=["English","Tamil","Hindi","Malayalam","Telugu"]
export default function Onboarding() {
    const [isSaving, setIsSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    age: '',
   occupationStatus: 'COLLEGE',
    major: '',
    classLevel: '',
    goals2025: '',
    completed2025: false, // New Field
    goals2026: '',        // New Field
    location: '',         // New Field
    dreamCompany: '',     // New Field
    interests: [],
    media: [],
    hobbies: [],
    languages: []
  });
  const backAudio = useRef(new Audio(backSfx));
  const audioClick = useRef(new Audio(clickSfx));
  const audioSuccess = useRef(new Audio(successSfx));

  const playSfx = (sound) => {
    sound.current.currentTime = 0;
    sound.current.play().catch(() => {});
  };
  /* Inside OnboardingForm.js */
const handleMediaToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      media: prev.media.includes(tag) 
        ? prev.media.filter(i => i !== tag) 
        : [...prev.media, tag]
    }));
  };

  const handleHobbiesToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      hobbies: prev.hobbies.includes(tag) 
        ? prev.hobbies.filter(i => i !== tag) 
        : [...prev.hobbies, tag]
    }));
  };

  const handleLanguageToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(tag) 
        ? prev.languages.filter(i => i !== tag) 
        : [...prev.languages, tag]
    }));
  };
const handleFinalSave = async () => {
  if (!formData.fullName?.trim()) return alert("NAME REQUIRED");
  setIsSaving(true);
  playSfx(audioSuccess);

  try {
    const user = auth.currentUser;
    const uid = auth.currentUser.uid;
    await updateProfile(user, {
      displayName: formData.fullName.trim()
    });
    const occupationData = {
      type: formData.occupationStatus.toLowerCase(),
      education: {
        major: formData.major || 'N/A',
        // Dynamically assign year/class based on status
        year: Number(formData.classLevel) || null 
      }
    };

    await setDoc(doc(db, 'users', uid), {
      ...formData,
      occupation: occupationData,
      onboardedAt: serverTimestamp()
    }, { merge: true });
    await user.reload();

    navigate(`/dashboard/${uid}`);
  } catch (err) {
    console.error("SAVE ERROR:", err);
  } finally {
    setIsSaving(false);
  }
};
  useEffect(() => {
    const fetchExistingData = async () => {
      if (!auth.currentUser) return;
      try {
        const docRef = doc(db, 'users', auth.currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setFormData(prev => ({
            ...prev,
            ...data,
            fullName: data.fullName || '',
            age: data.age || '',
            major: data.occupation?.education?.major || data.major || '',
            goals2025: data.goals?.["2025"]?.text || data.goals2025 || '',
          }));
        }
      } catch (err) { console.error("Fetch error:", err); }
      finally { setLoading(false); }
    };
    fetchExistingData();
  }, []);

  const handleInterestToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(tag) 
        ? prev.interests.filter(i => i !== tag) 
        : [...prev.interests, tag]
    }));
  };

  const nextStep = () => {
    playSfx(audioClick);
    setStep(prev => prev + 1);
  };

const handleBack = () => {
  playSfx(backAudio); // Plays edit.mp3 as requested
  setStep(prev => prev - 1);
};
  if (loading) return <div className="onboarding-page"><h1>RECALIBRATING...</h1></div>;

  return (
    <div className="onboarding-page">
      <AnimatePresence mode="wait">
        {/* STEP 1: IDENTITY */}
{step === 1 && (
  <motion.div key="step1" className="step-card">
    <span className="step-indicator">01 / 07</span> <br/>
  <button 
  className="quick-dashboard-link" 
  onClick={() => {
    const uid = auth.currentUser?.uid;
    if (uid) {
      playSfx(audioClick);
      navigate(`/dashboard/${uid}`);
    } else {
      alert("SESSION EXPIRED. PLEASE RE-LOGIN.");
    }
  }}
>
  ALREADY FILLED? <span>GO TO DASHBOARD ↗</span>
</button>
    <h1>Identify <br/><span>Yourself.</span></h1>
    
    <div className="input-stack">
      <div className="input-group">
        <label className="input-label">FULL NAME</label>
        <input value={formData.fullName} onChange={e => setFormData({...formData, fullName: e.target.value})} /> 
        
        <label className="input-label">AGE</label>
        <input type="number" value={formData.age} onChange={e => setFormData({...formData, age: e.target.value})} />

        <label className="input-label">STATUS</label>
        <select 
          className="elite-select" 
          value={formData.occupationStatus} 
          onChange={e => setFormData({...formData, occupationStatus: e.target.value, classLevel: ''})}
        >
          <option value="SKOOL">SKOOL</option>
          <option value="COLLEGE">COLLEGE</option>
          <option value="WORK">WORK</option>
          <option value="UNEMPLOYED">UNEMPLOYED</option>
        </select>

        {/* CONDITIONAL INPUTS BASED ON STATUS */}
        {(formData.occupationStatus === "SKOOL" || formData.occupationStatus === "COLLEGE") && (
          <div className="input-row">
            <div style={{flex: 1}}>
              <label className="input-label">MAJOR / STREAM</label>
              <input value={formData.major} onChange={e => setFormData({...formData, major: e.target.value})} />
            </div>
            <div style={{flex: 1}}>
              <label className="input-label">
                {formData.occupationStatus === "SKOOL" ? "CLASS" : "YEAR "}
              </label>
              <input 
                type="number" 
                value={formData.classLevel} 
                onChange={e => setFormData({...formData, classLevel: e.target.value})} 
              />
            </div>
          </div>
        )}
      </div>
    </div>
    
    <button className="next-btn" onClick={nextStep}>CONTINUE →</button>
  </motion.div>
)}

       {/* STEP 2: 2025 REVIEW */}
{step === 2 && (
  <motion.div key="step2" initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="step-card">
    <span className="step-indicator">02 / 07</span>
    <h1>2025 <br/><span>Reflection.</span></h1>
    <div className="input-stack">
        <div className="input-group">
            <label className="input-label">2025 GOAL</label>
      <textarea 
        placeholder="WHAT WAS YOUR MAIN 2025 GOAL?" 
        value={formData.goals2025} 
        onChange={e => setFormData({...formData, goals2025: e.target.value})} 
      />
      {/* Ensure this onClick is present and playSfx is called if desired */}
      <div className="checkbox-row" onClick={() => {
        setFormData({...formData, completed2025: !formData.completed2025});
        playSfx(audioClick); // Optional: add a click sound here too
      }}>
        <div className={`custom-check ${formData.completed2025 ? 'checked' : ''}`}>
           {/* Visual Tick: Only shows if completed2025 is true */}
           {formData.completed2025 && <div className="tick-mark" />}
        </div>
        <span>I COMPLETED THIS GOAL</span>
      </div>
      </div>
    </div>
    <div className="button-row">
        {/* Use the new handleBack function here */}
        <button className="back-btn" onClick={handleBack}>BACK</button>
        <button className="next-btn" onClick={nextStep}>NEXT →</button>
    </div>
  </motion.div>
)}

        {/* STEP 3: 2026 VISION */}
        {step === 3 && (
          <motion.div key="step3" initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -30, opacity: 0 }} className="step-card">
            <span className="step-indicator">03 / 07</span>
            <h1>The <br/><span>Future.</span></h1>
            <div className="input-stack">
                <div className="input-group">
            <label className="input-label">2026 GOAL</label>
              <textarea placeholder="YOUR #1 GOAL FOR 2026..." value={formData.goals2026} onChange={e => setFormData({...formData, goals2026: e.target.value})} />
              <label className="input-label">DREAM LOCATION</label>
              <input placeholder="DREAM LOCATION (TO VISIT/LIVE)" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} />
              <label className="input-label">EMPLOYMENT🥀</label>
              <input placeholder="DREAM COMPANY (IF ANY)" value={formData.dreamCompany} onChange={e => setFormData({...formData, dreamCompany: e.target.value})} />
            </div>
            </div>
            <div className="button-row">
                <button className="back-btn" onClick={handleBack}>BACK</button>
                <button className="next-btn" onClick={nextStep}>NEXT →</button>
            </div>
          </motion.div>
        )}

        {/* STEP 4: INTERESTS */}
        {step === 4 && (
          <motion.div key="step4" initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="step-card">
            <span className="step-indicator">04 / 07</span>
            <h1>Core <br/><span>Signals.</span></h1>
            <div className="tag-grid">
              {INTEREST_OPTIONS.map(tag => (
                <button key={tag} className={`tag-chip ${formData.interests.includes(tag) ? 'active' : ''}`} onClick={() => handleInterestToggle(tag)}>
                  {tag}
                </button>
              ))}
            </div>
            <div className="button-row">
                <button className="back-btn" onClick={handleBack}>BACK</button>
                  <button className="next-btn" onClick={nextStep}>NEXT →</button>
                 </div>
          </motion.div>
        )}
{/* STEP 5: MEDIA SELECTION */}
        {step === 5 && (
          <motion.div key="step5" initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="step-card">
            <span className="step-indicator">05 / 07</span>
            <h1>Media <br/><span>Consumption.</span></h1>
            <div className="tag-grid">
              {media.map(tag => (
                <button key={tag} className={`tag-chip ${formData.media.includes(tag) ? 'active' : ''}`} onClick={() => handleMediaToggle(tag)}>
                  {tag}
                </button>
              ))}
            </div>
            <div className="button-row">
                <button className="back-btn" onClick={handleBack}>BACK</button>
                <button className="next-btn" onClick={nextStep}>NEXT →</button>
            </div>
          </motion.div>
        )}

        {/* STEP 6: HOBBIES */}
        {step === 6 && (
          <motion.div key="step6" initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="step-card">
            <span className="step-indicator">06 / 07</span>
            <h1>THINGS you<br/><span>Love To.</span></h1>
            <div className="tag-grid">
              {hobbies.map(tag => (
                <button key={tag} className={`tag-chip ${formData.hobbies.includes(tag) ? 'active' : ''}`} onClick={() => handleHobbiesToggle(tag)}>
                  {tag}
                </button>
              ))}
            </div>
            <div className="button-row">
                <button className="back-btn" onClick={handleBack}>BACK</button>
                <button className="next-btn" onClick={nextStep}>NEXT →</button>
            </div>
          </motion.div>
        )}

        {/* STEP 7: LANGUAGES */}
        {step === 7 && (
          <motion.div key="step7" initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} className="step-card">
            <span className="step-indicator">07 / 07</span>
            <h1>Linguistic <br/><span>Stack.</span></h1>
            <div className="tag-grid">
              {languages.map(tag => (
                <button key={tag} className={`tag-chip ${formData.languages.includes(tag) ? 'active' : ''}`} onClick={() => handleLanguageToggle(tag)}>
                  {tag}
                </button>
              ))}
            </div>
            <div className="button-row">
                <button className="back-btn" onClick={handleBack}>BACK</button>
                <div className="onboarding-actions">
                  <motion.button
                    className={`save-lore-btn ${isSaving ? 'loading' : ''}`}
                    whileHover={!isSaving ? { x: -6, y: -6 } : {}}
                    whileTap={!isSaving ? { x: 2, y: 2 } : {}}
                    onClick={handleFinalSave}
                    disabled={isSaving}
                  >
                    {isSaving ? "SYNCING LORE..." : "INITIALIZE SYSTEM 2025"}
                  </motion.button>
                </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
